import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ImpactBadge } from "@/components/Badges";
import { PageHeader } from "@/components/PageHeader";

const Metric = ({ label, value, testId }) => (
  <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid={testId}>
    <p className="text-xs uppercase tracking-widest text-ink-muted">{label}</p>
    <p className="font-mono text-3xl mt-2 text-ink">{value}</p>
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary")
      .then((r) => setData(r.data))
      .catch((e) => {
        setError(e.response?.data?.detail || e.message);
        toast.error("Could not load dashboard");
      });
  }, []);

  if (error) return <p className="text-sm text-[#C62828]" data-testid="dashboard-error">{error}</p>;
  if (!data) return <p className="text-sm text-ink-muted font-mono" data-testid="dashboard-loading">Loading...</p>;

  // The API sends the total and the impact rows from one grouping of one list.
  // Summing the rows here instead would drop any impact this file does not
  // happen to list, and the card would then disagree with the table below it.
  const impactRows = data.open_by_impact || [];

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Dashboard" description="Portfolio-wide conformance snapshot." />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Metric label="Clients" value={data.clients} testId="metric-clients" />
        <Metric label="Sites" value={data.sites} testId="metric-sites" />
        <Metric label="Audits run" value={data.audits} testId="metric-audits" />
        <Metric label={data.unit_label || "Open issues"} value={data.open_total} testId="metric-open-issues" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-sm p-5">
          <h2 className="font-heading text-base font-semibold mb-4">Open issues by impact</h2>
          <table className="w-full text-left border-collapse">
            <caption className="sr-only">Open issue counts grouped by impact severity</caption>
            <tbody>
              {impactRows.map(([impact, n]) => (
                <tr key={impact} className="border-b border-gray-200 last:border-0">
                  <td className="py-2"><ImpactBadge impact={impact} /></td>
                  <td className="py-2 font-mono text-right" data-testid={`impact-count-${impact}`}>{n}</td>
                </tr>
              ))}
              <tr className="border-t border-gray-300">
                <td className="py-2 text-sm font-semibold">Total</td>
                <td className="py-2 font-mono text-right font-semibold" data-testid="impact-count-total">{data.open_total}</td>
              </tr>
            </tbody>
          </table>
          {data.unassigned_audits > 0 && (
            <p className="mt-4 text-sm border border-[#F57C00] rounded-sm p-3 bg-[#FFF8F0]" data-testid="unassigned-notice">
              {data.unassigned_audits} audit{data.unassigned_audits > 1 ? "s" : ""} awaiting assignment.{" "}
              <Link to="/audits" className="underline text-[#16324F] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F]">Review</Link>
            </p>
          )}
        </div>

        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-sm p-5">
          <h2 className="font-heading text-base font-semibold mb-4">Recent audits</h2>
          {data.recent_audits.length === 0 ? (
            <p className="text-sm text-ink-muted py-8 text-center border border-dashed border-gray-300 rounded-sm">
              No audits yet. Install the extension, open any page, and run your first audit.
            </p>
          ) : (
            <div className="relative overflow-x-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] focus-visible:ring-inset" tabIndex={0} role="region" aria-label="Most recent audits">
              <table className="w-full min-w-[560px] text-left border-collapse text-sm" data-testid="recent-audits-table">
                <caption className="sr-only">Most recent audits</caption>
                <thead>
                  <tr className="border-b border-gray-300">
                    <th scope="col" className="py-2 pr-4 font-heading font-semibold">Page</th>
                    <th scope="col" className="py-2 pr-4 font-heading font-semibold">Site</th>
                    <th scope="col" className="py-2 pr-4 font-heading font-semibold text-right">Violations</th>
                    <th scope="col" className="py-2 font-heading font-semibold text-right">When</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_audits.map((a) => (
                    <tr key={a.id} className="border-b border-gray-200 last:border-0">
                      <td className="py-2 pr-4 font-mono text-xs max-w-[260px] truncate" title={a.page_url}>
                        {a.page_url}
                        {a.authenticated && (
                          <span className="ml-2 border border-[#16324F] text-[#16324F] px-1.5 py-0.5 rounded-sm text-[10px] uppercase">Authenticated</span>
                        )}
                      </td>
                      <td className="py-2 pr-4">{a.site_name || <span className="text-ink-subtle">Unassigned</span>}</td>
                      <td className="py-2 pr-4 font-mono text-right">{a.totals?.total ?? 0}</td>
                      <td className="py-2 font-mono text-xs text-right text-ink-muted">{new Date(a.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
